import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtNumber } from "../lib/utils";
import { ShieldCheck, Lock, Filter, ChevronRight } from "lucide-react";

const POS_META = {
  top_quartile:    { label: "Top quartile",    cls: "pill-success" },
  above_median:    { label: "Above median",    cls: "pill-info" },
  below_median:    { label: "Below median",    cls: "pill-warn" },
  bottom_quartile: { label: "Bottom quartile", cls: "pill-error" },
};

export default function Benchmarks() {
  const [data, setData] = useState(null);
  const [issueFilter, setIssueFilter] = useState("");
  const [issues, setIssues] = useState([]);

  useEffect(() => {
    api.get("/benchmarks").then(r => setData(r.data));
    api.get("/catalogue/kpis").then(r => setIssues(r.data.material_issues));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.rows.filter(r => !issueFilter || r.material_issue === issueFilter);
  }, [data, issueFilter]);

  if (!data) return <div className="p-6 text-ink-soft">Loading benchmarks…</div>;

  // Group by material issue for summary tiles
  const summary = {
    top_quartile: 0, above_median: 0, below_median: 0, bottom_quartile: 0, suppressed: 0,
  };
  data.rows.forEach(r => {
    if (r.suppressed) summary.suppressed++;
    else if (r.your_position) summary[r.your_position]++;
  });

  return (
    <div className="p-6 space-y-5" data-testid="benchmarks-root">
      <div>
        <div className="section-label">Industry Intelligence</div>
        <h1 className="text-2xl font-bold tracking-tight mt-1">Anonymous Cohort Benchmarks</h1>
        <p className="text-sm text-ink-soft">
          Compare your KPIs against the IT/BPM cohort. All statistics are aggregated under
          k-anonymity (k≥{data.k_anonymity}); individual tenant values never leave their tenant DB.
        </p>
        <div className="mt-2 inline-flex items-center gap-2 pill" data-testid="k-policy-pill">
          <Lock size={11}/> {data.k_policy} · Pool: {data.tenant_pool_size} tenants
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { l: "Top quartile",    k: "top_quartile",    a: "bg-status-success" },
          { l: "Above median",    k: "above_median",    a: "bg-status-info"    },
          { l: "Below median",    k: "below_median",    a: "bg-status-warning" },
          { l: "Bottom quartile", k: "bottom_quartile", a: "bg-status-error"   },
          { l: "Suppressed (k<2)", k: "suppressed",     a: "bg-ink-soft"       },
        ].map(t => (
          <div key={t.k} className="card p-4" data-testid={`bench-sum-${t.k}`}>
            <div className="flex items-center justify-between">
              <div className="section-label">{t.l}</div>
              <div className={"h-7 w-7 rounded-md text-white flex items-center justify-center " + t.a}>
                <ShieldCheck size={14}/>
              </div>
            </div>
            <div className="metric-value">{summary[t.k] ?? 0}</div>
            <div className="text-xs text-ink-soft">of {data.rows.length} KPIs</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card p-3 flex flex-wrap items-center gap-2" data-testid="bench-filters">
        <div className="flex items-center gap-2 pr-3 border-r border-border">
          <Filter size={14} className="text-ink-soft"/>
          <span className="dlabel">Material Issue</span>
        </div>
        <button
          data-testid="bench-filter-all"
          onClick={()=>setIssueFilter("")}
          className={"pill " + (!issueFilter ? "pill-success" : "")}>All</button>
        {issues.map(m => (
          <button
            key={m}
            data-testid={`bench-filter-${m.replace(/\s+/g,'-').toLowerCase()}`}
            onClick={()=>setIssueFilter(m)}
            className={"pill " + (issueFilter === m ? "pill-success" : "")}>{m}</button>
        ))}
      </div>

      {/* Cohort table */}
      <div className="card overflow-hidden">
        <table className="dense">
          <thead>
            <tr>
              <th>KPI</th>
              <th>Material Issue</th>
              <th>Cohort size</th>
              <th className="text-right">Min</th>
              <th className="text-right">P25</th>
              <th className="text-right">Median</th>
              <th className="text-right">P75</th>
              <th className="text-right">Max</th>
              <th className="text-right">Your value</th>
              <th>Distribution</th>
              <th>Position</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => {
              if (r.suppressed) return (
                <tr key={r.kpi_id} data-testid={`bench-row-${r.kpi_id}`} className="opacity-60">
                  <td className="font-medium">{r.kpi_name}</td>
                  <td className="text-ink-soft">{r.material_issue}</td>
                  <td className="font-mono text-xs">{r.cohort_size}</td>
                  <td colSpan={7} className="text-ink-soft text-xs italic"><Lock size={11} className="inline mr-1"/> suppressed — cohort too small (k≥{r.k_anonymity})</td>
                  <td><span className="pill">n/a</span></td>
                  <td></td>
                </tr>
              );
              const meta = r.your_position ? POS_META[r.your_position] : null;
              // distribution viz: position your value on a min-max scale
              const range = r.max - r.min || 1;
              const yourPct = r.tenant_value != null ? ((r.tenant_value - r.min) / range) * 100 : null;
              const p25Pct = ((r.p25 - r.min) / range) * 100;
              const p75Pct = ((r.p75 - r.min) / range) * 100;
              const p50Pct = ((r.p50 - r.min) / range) * 100;
              return (
                <tr key={r.kpi_id} data-testid={`bench-row-${r.kpi_id}`}>
                  <td>
                    <Link to={`/kpi/${r.kpi_id}`} className="font-medium hover:text-brand">{r.kpi_name}</Link>
                    <div className="text-[11px] text-ink-soft">{r.unit}</div>
                  </td>
                  <td className="text-ink-soft">{r.material_issue}</td>
                  <td className="font-mono text-xs">{r.cohort_size}</td>
                  <td className="num text-ink-soft">{fmtNumber(r.min)}</td>
                  <td className="num text-ink-soft">{fmtNumber(r.p25)}</td>
                  <td className="num font-semibold">{fmtNumber(r.p50)}</td>
                  <td className="num text-ink-soft">{fmtNumber(r.p75)}</td>
                  <td className="num text-ink-soft">{fmtNumber(r.max)}</td>
                  <td className="num font-semibold">{r.tenant_value != null ? fmtNumber(r.tenant_value) : "—"}</td>
                  <td>
                    <div className="relative w-36 h-3 bg-surface rounded-full overflow-hidden border border-border">
                      {/* IQR band */}
                      <div className="absolute top-0 bottom-0 bg-brand-100" style={{left:`${p25Pct}%`, width:`${Math.max(0,p75Pct-p25Pct)}%`}}/>
                      {/* median tick */}
                      <div className="absolute top-0 bottom-0 w-[2px] bg-brand" style={{left:`${p50Pct}%`}}/>
                      {/* your dot */}
                      {yourPct != null && (
                        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full border-2 border-white bg-ink shadow-card" style={{left:`${Math.max(0, Math.min(100, yourPct))}%`}}/>
                      )}
                    </div>
                  </td>
                  <td>{meta ? (
                    <span className={"pill " + meta.cls}>{meta.label}</span>
                  ) : <span className="pill">no data</span>}</td>
                  <td>
                    <Link to={`/kpi/${r.kpi_id}`} className="btn btn-ghost"><ChevronRight size={12}/></Link>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={12} className="text-center text-ink-soft py-6">No KPIs in this view.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-ink-soft flex items-center gap-2">
        <Lock size={12}/> Aggregations apply k-anonymity (k≥{data.k_anonymity}) and winsorisation at 5/95 percentiles to protect tenant identity.
      </div>
    </div>
  );
}
